/**
 * A failed API call, in a shape the UI can act on.
 *
 * The `code` is what gets translated: the server sends a stable machine code
 * and never a user-facing sentence, so the French and English wording lives in
 * the SPA's own catalogue. `message` is the English developer text and must
 * never be rendered to a user.
 */
export class ApiClientError extends Error {
    code;
    status;
    params;
    fields;
    requestId;
    /**
     * How long the server asked the caller to wait, in seconds, from
     * `Retry-After`.
     *
     * Only a rate limiter sends it, and it is the difference between a client
     * that can be patient and one that can only give up: without it the sole
     * honest thing to do with a 429 is fail, because guessing how long to wait is
     * how a limiter turns into a client hammering it. The photo uploader uses it
     * to hold a queue rather than break it.
     *
     * Undefined when the header was absent or unparseable, which a caller must
     * treat as "no idea" rather than as zero.
     */
    retryAfterSeconds;
    constructor(init) {
        super(init.message, init.cause === undefined ? undefined : { cause: init.cause });
        this.name = 'ApiClientError';
        this.code = init.code;
        this.status = init.status;
        this.params = init.params;
        this.fields = init.fields;
        this.requestId = init.requestId;
        this.retryAfterSeconds = init.retryAfterSeconds;
    }
    /** Field errors keyed by path, for binding straight onto form inputs. */
    get fieldErrors() {
        const result = {};
        for (const field of this.fields ?? [])
            result[field.path] = field.code;
        return result;
    }
    /** True when retrying the exact same request could plausibly succeed. */
    get isRetryable() {
        return this.code === 'network' || this.status === 502 || this.status === 503 || this.status === 504;
    }
}
function isApiErrorBody(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const error = value.error;
    return typeof error === 'object' && error !== null && typeof error.code === 'string';
}
/**
 * Turns any failure - a structured API error, an HTML error page from the web
 * server, or a dead socket - into one error type, so callers never have to ask
 * what shape the failure arrived in.
 */
/**
 * `Retry-After`, in seconds, however the server chose to write it.
 *
 * RFC 9110 allows both a number of seconds and an HTTP date, and both are read
 * here - not for completeness, but because the two are indistinguishable to a
 * caller and getting it wrong in the date case produces a `NaN` wait that a
 * patient client would sleep on for ever.
 *
 * Clamped at zero: a date already in the past means "now", not a negative wait.
 * A header that is neither is `undefined`, which is honest - a caller must not
 * read "I could not parse this" as "no delay".
 */
function retryAfterFrom(response, now = Date.now()) {
    const header = response.headers.get('retry-after');
    if (header === null)
        return undefined;
    const seconds = Number(header);
    if (Number.isFinite(seconds))
        return Math.max(0, seconds);
    const at = Date.parse(header);
    return Number.isNaN(at) ? undefined : Math.max(0, Math.ceil((at - now) / 1000));
}
export async function toApiClientError(response) {
    const retryAfterSeconds = retryAfterFrom(response);
    let body;
    try {
        body = await response.json();
    }
    catch {
        // Apache and Passenger answer with HTML, not JSON, for things like a 502 or
        // the 360s gateway timeout. Those must not surface as "invalid JSON".
        body = undefined;
    }
    if (isApiErrorBody(body)) {
        return new ApiClientError({
            code: body.error.code,
            status: response.status,
            message: body.error.message,
            ...(body.error.params ? { params: body.error.params } : {}),
            ...(body.error.fields ? { fields: body.error.fields } : {}),
            ...(body.error.requestId ? { requestId: body.error.requestId } : {}),
            ...(retryAfterSeconds === undefined ? {} : { retryAfterSeconds }),
        });
    }
    return new ApiClientError({
        code: 'unknown',
        status: response.status,
        message: `Request failed with status ${String(response.status)}`,
        // Read even here, because a rate limiter in front of the application -
        // Apache, or a future CDN - answers with its own HTML and never with our
        // envelope. That answer still carries the one number worth having.
        ...(retryAfterSeconds === undefined ? {} : { retryAfterSeconds }),
    });
}
//# sourceMappingURL=errors.js.map